/**
 * Shared URL normalisation + validation for user-entered web links
 * (registration links, external event pages, websites…).
 *
 * Rules:
 *  - empty / whitespace / bare "https://" → null (treated as "not set")
 *  - missing scheme → https:// is prepended
 *  - only http(s) is allowed (blocks javascript:, data:, mailto:, …)
 *  - must have a dotted host (or localhost) so typos don't save as links
 *  - max 1000 chars
 */
export const MAX_URL_LENGTH = 1000;

export function normalizeWebUrl(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let value = String(raw).trim();
  if (!value) return null;

  // strip common paste artefacts
  value = value.replace(/\s+/g, "");
  if (/^(https?:\/\/)?$/i.test(value)) return null;

  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null; // mailto:, javascript:, data:…
    value = `https://${value}`;
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const host = url.hostname;
  if (!host) return null;
  if (host !== "localhost" && !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host)) return null;

  const out = url.toString();
  return out.length > MAX_URL_LENGTH ? null : out;
}

/** True when the input is empty or a valid web URL (for inline form feedback). */
export function isValidOrEmptyWebUrl(raw: string | null | undefined): boolean {
  const value = (raw ?? "").trim();
  if (!value || /^(https?:\/\/)?$/i.test(value)) return true;
  return normalizeWebUrl(value) !== null;
}
